/**
 * CircularProgress - SVG circular progress indicator
 */
import * as React from 'react';
import styled from '@emotion/styled';
import { Theme } from '../../../../Theme/Theme';

const Container = styled.div<{ size: number }>`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;

const SvgContainer = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  transform: rotate(-90deg);
`;

const ContentContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export type CircularProgressProps = {
  size: number;
  strokeWidth?: number;
  progress: number;
  color?: string;
  backgroundColor?: string;
  children?: React.ReactNode;
};

export const CircularProgress: React.FC<CircularProgressProps> = ({
  size,
  strokeWidth = 4,
  progress,
  color = Theme.Colors.primary,
  backgroundColor = 'rgba(255, 255, 255, 0.1)',
  children,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - Math.max(0, Math.min(1, progress)));

  return (
    <Container size={size}>
      <SvgContainer width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </SvgContainer>
      <ContentContainer>{children}</ContentContainer>
    </Container>
  );
};
